import React from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { GrClose } from "react-icons/gr";
import { VendorItemDetails } from "types/vendor";
import styles from "./HeritageLinkConfirmModal.module.scss";

export type HeritageLinkConfirmRequest = {
  localItem: VendorItemDetails;
  heritagePartNumber: string;
  heritageDescription?: string | null;
  existingPartNumber?: string | null;
  existingDescription?: string | null;
};

type Props = {
  request: HeritageLinkConfirmRequest;
  isSubmitting?: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

const HeritageLinkConfirmModal = ({
  request,
  isSubmitting = false,
  onClose,
  onConfirm,
}: Props) => {
  const { t } = useTranslation();
  const tBase = "views.vendors.VendorBody.VendorItems.VendorItemLinkModal.VendorItemLinkModalBody";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const isReplacingLink = Boolean(
    request.existingPartNumber &&
      request.existingPartNumber !== request.heritagePartNumber
  );

  const modal = (
    <div className={styles.overlay} role="presentation" onClick={onClose}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="heritage-link-confirm-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <div className="min-w-0 pr-3">
            <h2 id="heritage-link-confirm-title" className={styles.title}>
              {isReplacingLink ? tr("Replace Heritage link") : tr("Confirm Heritage link")}
            </h2>
            <p className={styles.subtitle}>
              {isReplacingLink
                ? tr("Replace Heritage link subtitle")
                : tr("Confirm Heritage link subtitle")}
            </p>
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label={tr("Close")}
            disabled={isSubmitting}
          >
            <GrClose size={18} />
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.linkCard}>
            <span className={styles.linkLabel}>{tr("Local item")}</span>
            <span className={styles.linkPart}>
              {request.localItem.item_name || getItemDisplayName(request.localItem)}
            </span>
            {(request.localItem.model || request.localItem.sku) && (
              <p className={styles.localMeta}>
                {[request.localItem.model, request.localItem.sku].filter(Boolean).join(" · ")}
              </p>
            )}
          </div>

          {isReplacingLink && (
            <>
              <p className={styles.warning} role="alert">
                {tr("Replace Heritage link warning")}
              </p>
              <div className={styles.linkCard}>
                <span className={styles.linkLabel}>{tr("Current Heritage link")}</span>
                <span className={styles.linkPart}>{request.existingPartNumber}</span>
                {request.existingDescription && (
                  <span className={styles.linkDescription}>{request.existingDescription}</span>
                )}
              </div>
            </>
          )}

          <div className={`${styles.linkCard} ${styles.linkCardNew}`}>
            <span className={styles.linkLabel}>
              {isReplacingLink ? tr("New Heritage link") : tr("Heritage part")}
            </span>
            <span className={styles.linkPart}>{request.heritagePartNumber}</span>
            {request.heritageDescription && (
              <span className={styles.linkDescription}>{request.heritageDescription}</span>
            )}
          </div>
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={onClose}
            disabled={isSubmitting}
          >
            {tr("Cancel")}
          </button>
          <button
            type="button"
            className={styles.confirmButton}
            onClick={onConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? tr("Linking...")
              : isReplacingLink
                ? tr("Replace link")
                : tr("Confirm link")}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

function getItemDisplayName(item: VendorItemDetails): string {
  return (
    item.vendor_part_number ||
    item.item_name ||
    item.short_description ||
    item.model ||
    item.sku ||
    (item.item_id != null ? `Item ${item.item_id}` : "Untitled")
  );
}

export default HeritageLinkConfirmModal;
